import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Box,
  Typography,
  Theme,
  SvgIcon,
} from '@mui/material';
import { SPARK_ROUTES } from 'constants/routes';
import { BoardIcon, MemoriesIcon, Star03 } from 'components/SVG';
import { useDialog } from 'hooks/common';
import { useAddBoards } from 'hooks/Board';
import { useWorkspaceContext } from 'hooks/common/useWorkspaceContext';
import CreateMemoryModal from './Memories/Modals/CreateMemoryModal';

type StyleOverrideStyle = {
  colorOnHover?: string;
  colorOnActive?: string;
  iconColorOnHover?: string;
  iconColorOnActive?: string;
};

type MenuButtonProps = {
  open: boolean;
  icon: React.ReactNode;
  path: string;
  text?: string;
  name?: string;
  styleOverride?: StyleOverrideStyle;
  onSelect: (path: string) => void;
  isCreateSparkRoute?: boolean;
  hasSubMenu?: boolean;
};

const subMenuItems = [
  {
    name: 'Board',
    description: 'Canvas for collaboration',
    path: '/board',
  },
  {
    name: 'Spark',
    description: 'AI workflow',
    path: SPARK_ROUTES.SPARK_BUILDER,
  },
  {
    name: 'Knowledge',
    description: 'Sources of information',
    path: '/knowledge',
  },
];

const MenuButton = ({
  open,
  icon,
  path,
  text,
  name,
  styleOverride,
  onSelect,
  isCreateSparkRoute = false,
  hasSubMenu = false,
}: MenuButtonProps) => {
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { mutateAsync: addBoard } = useAddBoards();
  const { currentWorkspaceID } = useWorkspaceContext();
  const openMenu = Boolean(anchorEl);

  const isCreateSpark = isCreateSparkRoute;
  const isActive = pathname.includes(path) || isCreateSpark;

  const getActiveStyle = (theme: Theme) => {
    if (styleOverride && styleOverride.colorOnActive) {
      return styleOverride.colorOnActive;
    }
    return theme.palette.primary.main;
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hasSubMenu) {
      setAnchorEl(event.currentTarget);
    } else {
      onSelect(path);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const {
    closeDialog: closeCreateMemoryDialog,
    open: isCreateMemoryDialogOpen,
    openDialog: openCreateMemoryDialog,
  } = useDialog();

  const handleMenuItemClick = async (itemPath: string) => {
    switch (itemPath) {
      case '/knowledge':
        openCreateMemoryDialog();
        break;
      case '/board':
        const response = await addBoard({});
        onSelect(`/boards/${response.data.id}`);
        break;
      default:
        onSelect(itemPath);
        break;
    }
    handleClose();
  };

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          display: 'block',
          '& .MuiButtonBase-root': {
            cursor: 'pointer !important',
          },
        }}
      >
        <ListItemButton
          onClick={handleClick}
          sx={{
            justifyContent: 'flex-start',
            margin: '0.5rem',
            borderRadius: '9px',
            display: 'flex',
            gap: open ? 2 : 0,
            width: open ? 'unset' : '3rem',
            height: '3rem',
            paddingLeft: '13px',
            cursor: 'pointer',
            backgroundColor: (theme) =>
              isCreateSpark
                ? getActiveStyle(theme)
                : isActive
                  ? theme.palette.primary.light
                  : '',
            color: (theme) =>
              isCreateSpark ? 'white' : theme.palette.primary.main,
            '&:hover': {
              backgroundColor: (theme) =>
                isCreateSpark
                  ? theme.palette.primary.dark
                  : theme.palette.primary.light,
              color: (theme) => theme.palette.primary.main,
              cursor: (openMenu || !openMenu || open) && 'pointer !important',
            },
          }}
        >
          <SvgIcon
            sx={{
              stroke: (theme) => theme.palette.primary.main,
              '&:hover': {
                stroke: (theme) =>
                  isCreateSpark
                    ? theme.palette.primary.dark
                    : theme.palette.primary.main,
              },
            }}
          >
            {icon}
          </SvgIcon>
          {open && (
            <>
              <ListItemText
                primary={text || name}
                sx={{
                  opacity: open ? 1 : 0,
                  color: (theme) =>
                    isCreateSpark ? 'white' : theme.palette.primary.main,
                }}
              />
            </>
          )}
        </ListItemButton>
      </ListItem>

      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            width: '300px',
            minWidth: '300px',
            marginLeft: '-5px',
            '& .MuiMenuItem-root': {
              py: 1,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.primary.light,
              },
            },
          },
        }}
      >
        {subMenuItems.map((item) => (
          <MenuItem
            key={item.name}
            onClick={() => handleMenuItemClick(item.path)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              paddingLeft: '13px',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.primary.light,
              },
            }}
          >
            <Box display='flex' alignItems='center' gap={1.5}>
              <span>
                <SvgIcon>
                  {item.name === 'Spark' ? (
                    <Star03 fill='black' />
                  ) : item.name === 'Knowledge' ? (
                    <MemoriesIcon
                      fill='black'
                      stroke='black'
                      height='20'
                      width='20'
                    />
                  ) : (
                    <BoardIcon width='17' height='17' />
                  )}
                </SvgIcon>
              </span>
              <span>
                <Typography
                  variant='body1'
                  fontWeight={500}
                  color='black'
                  padding={0}
                >
                  {item.name}
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block' }}
                  padding={0}
                >
                  {item.description}
                </Typography>
              </span>
            </Box>
          </MenuItem>
        ))}
      </Menu>
      {isCreateMemoryDialogOpen && (
        <CreateMemoryModal
          onClose={closeCreateMemoryDialog}
          open={isCreateMemoryDialogOpen}
        />
      )}
    </>
  );
};

export default MenuButton;
